import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiSave, FiArrowLeft, FiPlus, FiTrash2 } from "react-icons/fi";
import {
    getHostels,
    getHostelFeeStructures,
    createHostelFeeStructure,
    updateHostelFeeStructure,
} from "../../services/hostelService";
import { academicYearService } from "../../services/yearService";

const emptyHead = () => ({ name: "", feeType: "Non-refundable", amount: "" });

export default function HostelFeeStructureForm() {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const navigate = useNavigate();

    const [academicYears, setAcademicYears] = useState([]);
    const [hostels, setHostels] = useState([]);
    const [academicYear, setAcademicYear] = useState("");
    const [hostel, setHostel] = useState("");
    const [roomType, setRoomType] = useState("Double");
    const [heads, setHeads] = useState([emptyHead()]);
    const [isActive, setIsActive] = useState(true);
    const [fetching, setFetching] = useState(isEdit);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        Promise.all([academicYearService.getAll(), getHostels()])
            .then(([ayRes, hRes]) => {
                const years = ayRes.data || [];
                setAcademicYears(years);
                setHostels(hRes.data || []);
                if (!isEdit) {
                    const active = years.find((y) => y.isActive) || years[0];
                    if (active) setAcademicYear(active._id);
                }
            })
            .catch(() => toast.error("Failed to load hostels/academic years"));
    }, []); // eslint-disable-line

    useEffect(() => {
        if (!isEdit) return;
        // No single-structure GET endpoint was built (structures are looked up
        // via the list endpoint filtered by academicYear/hostel) — so for edit
        // mode we pull the full list and find the matching id client-side.
        (async () => {
            try {
                const res = await getHostelFeeStructures({});
                const structure = (res.data || []).find((s) => s._id === id);
                if (!structure) throw new Error("Not found");
                setAcademicYear(structure.academicYear._id || structure.academicYear);
                setHostel(structure.hostel._id || structure.hostel);
                setRoomType(structure.roomType);
                setHeads(
                    structure.heads.map((h) => ({
                        name: h.name,
                        feeType: h.feeType,
                        amount: h.amount,
                    })),
                );
                setIsActive(structure.isActive);
            } catch (err) {
                toast.error("Failed to load fee structure");
                navigate("/hostel-fee-structures");
            } finally {
                setFetching(false);
            }
        })();
    }, [id, isEdit, navigate]);

    const updateHead = (index, field, value) => {
        setHeads((prev) =>
            prev.map((h, i) => (i === index ? { ...h, [field]: value } : h)),
        );
    };

    const addHead = () => setHeads((prev) => [...prev, emptyHead()]);
    const removeHead = (index) =>
        setHeads((prev) => prev.filter((_, i) => i !== index));

    const total = heads.reduce((sum, h) => sum + (Number(h.amount) || 0), 0);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!academicYear || !hostel || !roomType) {
            return toast.error("Academic year, hostel and room type are required");
        }
        if (heads.some((h) => !h.name.trim() || !h.amount)) {
            return toast.error("Every fee head needs a name and amount");
        }

        const payload = {
            academicYear,
            hostel,
            roomType,
            heads: heads.map((h) => ({ ...h, amount: Number(h.amount) })),
            isActive,
        };

        setSaving(true);
        try {
            if (isEdit) {
                await updateHostelFeeStructure(id, payload);
                toast.success("Fee structure updated successfully");
            } else {
                await createHostelFeeStructure(payload);
                toast.success("Fee structure created successfully");
            }
            navigate("/hostel-fee-structures");
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to save fee structure");
        } finally {
            setSaving(false);
        }
    };

    if (fetching) {
        return (
            <div className="card">
                <div className="loading-center">
                    <div className="spinner" />
                    Loading fee structure...
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">
                        {isEdit ? "Edit Hostel Fee Structure" : "Add Hostel Fee Structure"}
                    </h1>
                    <p className="page-subtitle">
                        Define fee heads for a hostel + room type + academic year combination.
                    </p>
                </div>
                <button className="btn btn-outline" onClick={() => navigate("/hostel-fee-structures")}>
                    <FiArrowLeft /> Back
                </button>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="card">
                    <div className="card-body">
                        <div className="form-grid">
                            <div className="form-group">
                                <label className="form-label">
                                    Academic Year<span style={{ color: "var(--danger)" }}> *</span>
                                </label>
                                <select
                                    className="form-control"
                                    value={academicYear}
                                    onChange={(e) => setAcademicYear(e.target.value)}
                                    disabled={isEdit}
                                >
                                    <option value="">Select year</option>
                                    {academicYears.map((y) => (
                                        <option key={y._id} value={y._id}>
                                            {y.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    Hostel<span style={{ color: "var(--danger)" }}> *</span>
                                </label>
                                <select
                                    className="form-control"
                                    value={hostel}
                                    onChange={(e) => setHostel(e.target.value)}
                                    disabled={isEdit}
                                >
                                    <option value="">Select hostel</option>
                                    {hostels.map((h) => (
                                        <option key={h._id} value={h._id}>
                                            {h.name} ({h.type})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    Room Type<span style={{ color: "var(--danger)" }}> *</span>
                                </label>
                                <select
                                    className="form-control"
                                    value={roomType}
                                    onChange={(e) => setRoomType(e.target.value)}
                                    disabled={isEdit}
                                >
                                    <option value="Single">Single</option>
                                    <option value="Double">Double</option>
                                    <option value="Triple">Triple</option>
                                    <option value="Dormitory">Dormitory</option>
                                </select>
                            </div>
                        </div>
                        {isEdit && (
                            <p className="form-hint" style={{ marginTop: "0.5rem" }}>
                                Academic year, hostel and room type can't be changed after creation —
                                delete and recreate the structure if these need to change.
                            </p>
                        )}

                        <div className="form-group" style={{ marginTop: "1.25rem" }}>
                            <label className="form-label">
                                Fee Heads<span style={{ color: "var(--danger)" }}> *</span>
                            </label>
                            {heads.map((head, i) => (
                                <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                                    <input
                                        className="form-control"
                                        placeholder="e.g. Room Rent"
                                        value={head.name}
                                        onChange={(e) => updateHead(i, "name", e.target.value)}
                                        style={{ flex: 2 }}
                                    />
                                    <select
                                        className="form-control"
                                        value={head.feeType}
                                        onChange={(e) => updateHead(i, "feeType", e.target.value)}
                                        style={{ flex: 1 }}
                                    >
                                        <option value="Non-refundable">Non-refundable</option>
                                        <option value="Refundable">Refundable</option>
                                    </select>
                                    <input
                                        className="form-control"
                                        type="number"
                                        placeholder="Amount"
                                        value={head.amount}
                                        onChange={(e) => updateHead(i, "amount", e.target.value)}
                                        style={{ flex: 1 }}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-icon"
                                        onClick={() => removeHead(i)}
                                        disabled={heads.length === 1}
                                        style={{ color: "var(--danger)" }}
                                        title="Remove head"
                                    >
                                        <FiTrash2 />
                                    </button>
                                </div>
                            ))}
                            <button type="button" className="btn btn-outline btn-sm" onClick={addHead}>
                                <FiPlus /> Add Head
                            </button>
                        </div>

                        <p style={{ fontWeight: 600, marginTop: "0.75rem" }}>
                            Total: ₹{total.toLocaleString("en-IN")}
                        </p>

                        {isEdit && (
                            <div className="form-group" style={{ marginTop: "1rem" }}>
                                <label className="form-label">
                                    <input
                                        type="checkbox"
                                        checked={isActive}
                                        onChange={(e) => setIsActive(e.target.checked)}
                                        style={{ marginRight: 8 }}
                                    />
                                    Active
                                </label>
                            </div>
                        )}

                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "1.25rem" }}>
                            <button
                                type="button"
                                className="btn btn-outline"
                                onClick={() => navigate("/hostel-fee-structures")}
                            >
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={saving}>
                                <FiSave /> {saving ? "Saving..." : "Save Structure"}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}